/*
 * Copyright (c) 2024-2025 Nodals AI Ltd
 * All rights reserved.
 */

import { AnalyticsRecorder, type AnalyticsOptions, type ReportingEvents } from '../analytics';
import { Opportunity } from '../auction';
import { EventDispatcher } from '../events';
import { Logger } from '../logger';
import { AdCommunicator, NodalsMessageEvent, type AdCommunicatorEvents } from '../messaging';
import { generateUUID, getPrebidUserId } from '../util';
import { SiteConfig } from './config';
import { Placement } from './placement';
import { PlacementManager } from './placement/manager';

import {
  AdAssignedKeyValues,
  SelectionReportingPlacementData,
  type BidData,
  type CampaignData,
  type CampaignType,
  type KeyValue,
  type PlacementAssignedKeyValues,
  type SelectionEndpointPlacementData,
} from '../types';
import type {
  PrebidAdUnit,
  PrebidFormattedKeyValues,
  RtdModuleConfig,
  UserConsent,
} from '../types/prebid';

const logger = Logger.getInstance();

interface SessionData {
  id: string;
}

class PageSession {
  private static readonly STORAGE_KEY = 'nodals.session';

  public getId(): string {
    try {
      const existingSession = sessionStorage.getItem(PageSession.STORAGE_KEY);
      if (existingSession) {
        const sessionData: SessionData = JSON.parse(existingSession);
        if (sessionData.id) {
          return sessionData.id;
        }
      }
    } catch (error) {
      logger.warn(`Session data retrieval failed: ${error} - creating new session`);
    }

    return this.createNewSession();
  }

  private createNewSession(): string {
    const sessionId = generateUUID();
    const sessionData: SessionData = { id: sessionId };

    try {
      sessionStorage.setItem(PageSession.STORAGE_KEY, JSON.stringify(sessionData));
    } catch (error) {
      // SessionStorage might not be available (e.g., private browsing)
      logger.warn(`Session data storage failure: ${error}`);
    }

    return sessionId;
  }
}

class Page extends EventDispatcher<ReportingEvents> {
  private adCommunicator: AdCommunicator = AdCommunicator.getInstance();
  protected placementMap: Map<string, Placement> = new Map<string, Placement>();
  private _userConsent: UserConsent | null = null;
  protected placementManager: PlacementManager;
  protected siteConfig: SiteConfig;
  protected config: RtdModuleConfig | null = null;
  private analyticsRecorder: AnalyticsRecorder | null = null;
  private session: PageSession = new PageSession();
  private pageViewId: string = generateUUID();
  private pageViewRecorded: boolean = false;
  private ppuid: string | undefined = undefined;
  private campaignDataHash: number = 0;

  protected constructor(siteConfig: SiteConfig) {
    super();
    this.siteConfig = siteConfig;
    this.placementManager = PlacementManager.getInstance(
      (
        adUnit: PrebidAdUnit,
        adUnitTransformer: (adUnitCode: string) => string,
        campaignData: CampaignData[],
        siteConfig: SiteConfig
      ) => {
        return this.createPlacement(adUnit, adUnitTransformer, campaignData, siteConfig);
      }
    );
    this.addListeners();
  }

  private static instance: Page;

  public static getInstance(siteConfig: SiteConfig): Page {
    if (!Page.instance) {
      Page.instance = new Page(siteConfig);
    }
    return Page.instance;
  }

  get isVisible(): boolean {
    return document.visibilityState === 'visible' || document.hidden === false;
  }

  public setUserConsent(userConsent: UserConsent): void {
    this._userConsent = userConsent;
    if (!this.pageViewRecorded) {
      // Record page view on consent update if not already recorded
      this.recordPageView();
    }
  }

  public initialiseAuction(adUnits: PrebidAdUnit[], campaignData: CampaignData[]): Opportunity {
    const placements = this.getOrCreatePlacements(adUnits, campaignData);
    const opportunity = new Opportunity(generateUUID());

    // Dispatch auction start event for analytics
    this.dispatch('AuctionStart', {
      opportunityId: opportunity.id,
      placements: placements,
      userConsent: this._userConsent || ({} as UserConsent),
    });

    placements.forEach((placement) => {
      try {
        placement.newOpportunity(opportunity);
      } catch (error) {
        logger.error(`Failed to create opportunity for placement: ${error}`);
      }
    });

    logger.debug(
      `Auction initialised with ${placements.length} placements and opportunity ${opportunity.id}`
    );
    return opportunity;
  }

  public endAuction(adUnitCodes: string[], bids: Record<string, BidData[]> = {}): void {
    const uniquePlacements = this.getUniquePlacements(adUnitCodes);
    if (uniquePlacements.length === 0) {
      logger.warn(
        `No placements found for adUnits: ${adUnitCodes.join(', ')}, post-auction actions skipped.`
      );
      return;
    }
    const opportunityId = uniquePlacements[0]?.currentOpportunity?.id;
    if (!opportunityId) {
      logger.warn(`No opportunity found for adUnits: ${adUnitCodes.join(', ')}`);
      return;
    }

    this.dispatch('AuctionEnd', {
      opportunityId: opportunityId,
      placements: uniquePlacements,
      bids: bids,
      userConsent: this._userConsent || ({} as UserConsent),
      pvid: this.pageViewId,
      sid: this.getSessionId(),
      uid: this.ppuid,
    });

    logger.debug(
      `Auction ended for opportunity ${opportunityId} with adUnits: ${adUnitCodes.join(', ')}`
    );
  }

  public enableAnalytics(analyticsOptions: AnalyticsOptions): void {
    if (!this.analyticsRecorder) {
      this.analyticsRecorder = new AnalyticsRecorder(this as any, analyticsOptions);
      logger.debug('Analytics recorder enabled for Page');
    }
  }

  public setConfig(config: RtdModuleConfig): void {
    this.config = config;
    if (this.config?.params.userIdEidSource) {
      this.ppuid = getPrebidUserId(this.config.params.userIdEidSource) ?? undefined;
    }
  }

  public setPlacementKeyValues(
    adUnitCodes: string[],
    placementKeyValues: PlacementAssignedKeyValues[]
  ): void {
    this.validatePlacements();
    adUnitCodes.forEach((adUnitCode) => {
      const placement = this.placementMap.get(adUnitCode);
      if (placement) {
        const keyValuesForAdUnit = placementKeyValues.find((kv) => kv.gad === adUnitCode);
        if (keyValuesForAdUnit) {
          placement.setKeyValues(keyValuesForAdUnit);
        }
      } else {
        logger.warn(`No placement found for adUnit: ${adUnitCode}`);
      }
    });
  }

  /**
   * Given an array of adUnit codes, return an array of unique Placement objects
   */
  public getKeyValuesForAdUnits(
    adUnitArray: string[],
    campaignTypes: CampaignType[]
  ): PrebidFormattedKeyValues {
    const targetingData: PrebidFormattedKeyValues = {};
    if (!this.isVisible) {
      logger.info('Page is not visible, skipping key value retrieval');
      return targetingData;
    }

    // Create a map to track which adUnits are served by which placements
    const placementToAdUnitCodes = new Map<Placement, string[]>();

    adUnitArray.forEach((adUnitCode) => {
      const placement = this.placementMap.get(adUnitCode);
      if (placement) {
        if (!placementToAdUnitCodes.has(placement)) {
          placementToAdUnitCodes.set(placement, []);
        }
        placementToAdUnitCodes.get(placement)!.push(adUnitCode);
      }
    });

    let hasRelevantCampaigns = false;

    placementToAdUnitCodes.forEach((adUnitCodes, placement) => {
      const keyValues: KeyValue[] = placement.getKeyValuesForCampaignType(campaignTypes);
      if (keyValues.length > 0) {
        // Apply same key values to all adUnits served by this placement (handles aliasing)
        adUnitCodes.forEach((adUnitCode) => {
          targetingData[adUnitCode] = {};
          keyValues.forEach((kv) => {
            targetingData[adUnitCode][kv.key] = kv.value;
          });
        });
      }

      if (placement.hasAnyCampaignForTypes(campaignTypes)) {
        hasRelevantCampaigns = true;
      }
    });

    if (hasRelevantCampaigns) {
      this.dispatchTargetingSelectionComplete(adUnitArray, campaignTypes);
    }

    return targetingData;
  }

  public getFilteredKeyValuesByPlacementByAd(
    adUnitArray: string[],
    campaignTypes?: CampaignType[]
  ): SelectionReportingPlacementData[] {
    const reportingData: SelectionReportingPlacementData[] = [];
    const uniquePlacements = this.getUniquePlacements(adUnitArray);

    uniquePlacements.forEach((placement) => {
      let adAssignedKeyValues: AdAssignedKeyValues[] = [];

      // Filter by campaign types if provided
      if (campaignTypes && campaignTypes.length > 0) {
        adAssignedKeyValues = placement.getAdKeyValuesByAdForCampaignTypes(campaignTypes);
      } else {
        adAssignedKeyValues = placement.getAdKeyValuesByAd();
      }

      // Filter out AdAssignedKeyValues with empty kvs arrays
      const filteredAdAssignedKeyValues = adAssignedKeyValues.filter(
        (ad) => ad.kvs && ad.kvs.length > 0
      );

      if (filteredAdAssignedKeyValues.length > 0) {
        reportingData.push({
          gad: placement.adUnitCode,
          ads: filteredAdAssignedKeyValues,
        });
      }
    });

    return reportingData;
  }

  public getEligibleCampaignsByAdUnit(
    adUnitCodes: string[],
    campaignTypes: CampaignType[]
  ): SelectionEndpointPlacementData[] {
    const placementEligibleCampaigns: SelectionEndpointPlacementData[] = [];
    const uniquePlacements = this.getUniquePlacements(adUnitCodes);

    uniquePlacements.forEach((placement) => {
      placementEligibleCampaigns.push({
        gad: placement.adUnitCode,
        ss: placement.supportedSizes
          .filter((size) => size != null)
          .map((size) => ({
            w: size.width,
            h: size.height,
          })),
        v: placement.visibility,
        campaigns: placement.getEligibleAdIdsByCampaign(campaignTypes),
      });
    });

    return placementEligibleCampaigns;
  }

  protected getSessionId(): string {
    return this.session.getId();
  }

  protected createPlacement(
    // @ts-ignore
    adUnit: PrebidAdUnit,
    // @ts-ignore
    adUnitTransformer: (adUnitCode: string) => string,
    // @ts-ignore
    campaignData: CampaignData[],
    // @ts-ignore
    siteConfig: SiteConfig
  ): Placement | undefined {
    throw new Error('Generic page cannot create non-video placements directly');
  }

  protected locateContainingPlacement(event: {
    data: AdCommunicatorEvents['TaggedAdServed'] | AdCommunicatorEvents['AdInteraction'];
  }): Placement | null {
    const targetWindow = event.data.window;

    // First try: window-based lookup using iframe matching
    const iframes = document.querySelectorAll('iframe');

    for (const iframe of iframes) {
      if (iframe.contentWindow === targetWindow) {
        for (const [, placement] of this.placementMap) {
          if (placement.domElement?.contains(iframe)) {
            logger.info(
              `TaggedAdServed event iframe match found for adUnit: ${placement.adUnitCode}`
            );
            return placement;
          }
        }
      }
    }

    return null;
  }

  protected getUniquePlacements(adUnitCodes: string[]): Placement[] {
    this.validatePlacements();
    const uniquePlacements = new Set<Placement>();

    adUnitCodes.forEach((adUnitCode) => {
      const placement = this.placementMap.get(adUnitCode);
      if (placement) {
        uniquePlacements.add(placement);
      }
    });

    return Array.from(uniquePlacements);
  }

  private getEventContextData<T extends Record<string, any>>(
    placement: Placement,
    eventData: { deliveryId: string },
    additionalData: T = {} as T
  ): {
    opportunityId: string;
    deliveryId: string;
    adUnit: string;
    pvid: string;
    sid: string;
    uid?: string;
    userConsent: UserConsent;
  } & T {
    const currentOpportunity = placement.currentOpportunity;
    if (!currentOpportunity) {
      throw new Error('No current opportunity found for placement');
    }

    return {
      opportunityId: currentOpportunity.id,
      deliveryId: eventData.deliveryId,
      adUnit: placement.adUnitCode,
      pvid: this.pageViewId,
      sid: this.getSessionId(),
      uid: this.ppuid,
      userConsent: this._userConsent || ({} as UserConsent),
      ...additionalData,
    };
  }

  private setupPlacementEventListeners(placement: Placement): void {
    placement.on('DirectServedAdImpression', (event) => {
      const currentOpportunity = placement.currentOpportunity;
      if (currentOpportunity) {
        this.dispatch('AdImpression', {
          ...this.getEventContextData(placement, event.data, {
            id: event.data.id,
            advertiserId: event.data.advertiserId,
            lineItemId: event.data.lineItemId,
            kvs: event.data.kvs,
            creativeId: event.data.creativeId,
            renderedData: { size: event.data.size },
          }),
        });
      }
    });

    placement.on('ProgrammaticServedAdImpression', (event) => {
      const currentOpportunity = placement.currentOpportunity;
      if (currentOpportunity) {
        this.dispatch('AdImpression', {
          ...this.getEventContextData(placement, event.data, {
            id: event.data.id,
            advertiserDomains: event.data.advertiserDomains,
            renderedData: { size: event.data.size },
            nodalsDealId: event.data.nodalsDealId,
          }),
        });
      }
    });

    placement.on('AdInteraction', (event) => {
      const currentOpportunity = placement.currentOpportunity;
      if (currentOpportunity) {
        this.dispatch('AdInteraction', {
          ...this.getEventContextData(placement, event.data, {
            impressionId: event.data.impressionId,
            advertiserId: event.data.advertiserId,
            advertiserDomains: event.data.advertiserDomains,
            lineItemId: event.data.lineItemId,
            nodalsDealId: event.data.nodalsDealId,
            useHttpPost: event.data.useHttpPost,
          }),
        });
      }
    });

    placement.on('ViewableImpression', (event) => {
      const currentOpportunity = placement.currentOpportunity;
      if (currentOpportunity) {
        this.dispatch('AdViewableImpression', {
          ...this.getEventContextData(placement, event.data, {
            impressionId: event.data.impressionId,
            advertiserId: event.data.advertiserId,
            advertiserDomains: event.data.advertiserDomains,
            lineItemId: event.data.lineItemId,
            nodalsDealId: event.data.nodalsDealId,
            useHttpPost: event.data.useHttpPost,
          }),
        });
      }
    });

    placement.on('VideoAdPlaybackQuartileReached', (event) => {
      logger.info(
        `Placement dispatching AdPlaybackQuartileReached quartile: ${event.data.quartile}`
      );
      const currentOpportunity = placement.currentOpportunity;
      if (currentOpportunity) {
        this.dispatch('VideoQuartile', {
          ...this.getEventContextData(placement, event.data, {
            impressionId: event.data.impressionId,
            quartile: event.data.quartile,
            advertiserId: event.data.advertiserId,
            advertiserDomains: event.data.advertiserDomains,
            lineItemId: event.data.lineItemId,
            nodalsDealId: event.data.nodalsDealId,
            useHttpPost: event.data.useHttpPost,
          }),
        });
      }
    });

    placement.on('BrandSafetyViolation', (event) => {
      const currentOpportunity = placement.currentOpportunity;
      if (currentOpportunity) {
        this.dispatch('BrandSafetyViolation', {
          cid: event.data.campaignId,
          pvid: this.pageViewId,
          sid: this.getSessionId(),
          uid: this.ppuid,
          adUnit: placement.adUnitCode,
          detectedWords: event.data.detectedWords,
          userConsent: this._userConsent || ({} as UserConsent),
          bidStage: event.data.bidStage,
          opportunityId: currentOpportunity.id,
        });
      }
    });
  }

  private getOrCreatePlacements(
    adUnits: PrebidAdUnit[],
    campaignData: CampaignData[]
  ): Placement[] {
    const placements: Placement[] = [];
    const currentHash = this.hashCampaignData(campaignData);
    const campaignDataChanged = currentHash !== this.campaignDataHash;

    adUnits.forEach((adUnit) => {
      let placement = this.placementMap.get(adUnit.code);

      if (!placement) {
        try {
          const adUnitTransformer =
            this.config?.params?.adUnitTransformer ?? ((adUnitCode: string) => adUnitCode);
          placement = this.placementManager.getPlacement(
            adUnit,
            campaignData,
            adUnitTransformer,
            this.siteConfig
          );
          if (placement) {
            const isAliasedPlacement = [...this.placementMap.values()].includes(placement);
            if (!isAliasedPlacement) {
              this.setupPlacementEventListeners(placement);
              if (!placement.init()) {
                logger.warn(
                  `Slot detection failed on Placement initialisation for adUnit: ${adUnit.code}`
                );
              }
            }
            this.placementMap.set(adUnit.code, placement);
          } else {
            logger.error(`Failed to initialise placement for adUnit: ${adUnit.code}`);
            return;
          }
        } catch (error) {
          logger.error(`Failed to create placement for adUnit ${adUnit.code}: ${error}`);
          return;
        }
      } else if (campaignDataChanged) {
        // Only update campaigns if campaign data has actually changed
        placement.updateCampaigns(campaignData);
      }

      placements.push(placement);
    });

    // Update stored hash if campaign data changed
    if (campaignDataChanged) {
      this.campaignDataHash = currentHash;
    }

    return [...new Set(placements)]; // Return unique placements
  }

  private recordPageView(): void {
    if (this.pageViewRecorded) {
      logger.warn('Page view has already been recorded.');
      return;
    }

    this.pageViewRecorded = true;

    let userId = undefined;
    if (this.config?.params.userIdEidSource) {
      userId = getPrebidUserId(this.config.params.userIdEidSource) ?? undefined;
    }

    this.dispatch('PageView', {
      id: this.pageViewId,
      sid: this.getSessionId(),
      uid: userId,
      userConsent: this._userConsent || ({} as UserConsent),
    });
  }

  private dispatchTargetingSelectionComplete(
    adUnitArray: string[],
    campaignTypes: CampaignType[]
  ): void {
    // Get current opportunity ID from placements
    let opportunityId: string | null = null;
    const uniquePlacements = this.getUniquePlacements(adUnitArray);

    for (const placement of uniquePlacements) {
      if (placement.currentOpportunity && !opportunityId) {
        opportunityId = placement.currentOpportunity.id;
        break;
      }
    }

    // Only dispatch if we have an opportunity ID and user consent
    if (opportunityId && this._userConsent) {
      const selectionData = this.getFilteredKeyValuesByPlacementByAd(adUnitArray, campaignTypes);

      if (selectionData.length > 0) {
        this.dispatch('TargetingSelectionComplete', {
          opportunityId,
          selectionData,
          userConsent: this._userConsent,
        });
      } else {
        logger.info('No filtered targeting data to report.');
      }
    }
  }

  private addListeners(): void {
    this.adCommunicator.on('TaggedAdServed', (event) => {
      this.taggedAdServedEventHandler(event);
    });
    this.adCommunicator.on('AdInteraction', (event) => {
      this.placementAdInteractionHandler(event);
    });
  }

  private validatePlacements(): void {
    this.placementMap.forEach((placement) => {
      if (!placement.hasValidSlot()) {
        placement.determineSlot();
      }
    });
  }

  private hashCampaignData(campaignData: CampaignData[]): number {
    const str = JSON.stringify(campaignData);
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return hash;
  }

  // @ts-ignore
  protected placementAdInteractionHandler(event: {
    data: AdCommunicatorEvents['AdInteraction'];
  }): void {}

  private taggedAdServedEventHandler(event: {
    data: AdCommunicatorEvents['TaggedAdServed'];
  }): void {
    logger.info(`TaggedAdServed event received by Page for adUnitPath: ${event.data.adUnitPath}`);

    const placement = this.locateContainingPlacement(event);

    if (placement) {
      const currentOpportunity = placement.currentOpportunity;
      if (!currentOpportunity) {
        // If no Prebid auction took place, but a Nodals ad in Prebid mode was served
        // by the 1st party ad server, we cannot log an impression
        logger.warn(`No current opportunity found for placement: ${placement.adUnitCode}`);
        return;
      }
      logger.debug(`TaggedAdServed event matched to placement for adUnit: ${placement.adUnitCode}`);
      placement.handleTaggedAdDelivery(
        event.data.deliveryId as string,
        event.data.impressionId as string
      );

      // Send opportunity data back to the ad
      this.adCommunicator.sendMessage(
        event.data.window,
        NodalsMessageEvent.OPPORTUNITY_DATA,
        {
          id: currentOpportunity.id,
          gad: placement.adUnitCode,
        },
        this.config
      );
    } else {
      logger.warn(
        `No matching placement found for impression for adUnitPath: ${event.data.adUnitPath}`
      );
    }
  }
}

export { Page, PageSession };
